/**
 * Extracts the filename from a URL path
 * @param url - The URL string to extract filename from
 * @returns The filename (last part after the last slash)
 * 
 * @example
 * extractFilenameFromUrl('/myfile/deh/voucher.pdf') // Returns: 'voucher.pdf'
 * extractFilenameFromUrl('https://example.com/docs/report.xlsx') // Returns: 'report.xlsx'
 * extractFilenameFromUrl('/path/to/file') // Returns: 'file'
 * extractFilenameFromUrl('') // Returns: ''
 */
export function extractFilenameFromUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Remove query parameters and hash fragments
  const cleanUrl = url.split('?')[0].split('#')[0];

  // Split by slash and get the last part
  const parts = cleanUrl.split('/');
  const filename = parts[parts.length - 1];

  return filename || '';
}

/**
 * Creates a custom timestamp format
 * @param format - Custom format string using placeholders
 * @returns Formatted timestamp string
 * 
 * Available placeholders:
 * - YYYY: 4-digit year
 * - MM: 2-digit month
 * - DD: 2-digit day
 * - HH: 2-digit hour (24h)
 * - mm: 2-digit minute
 * - ss: 2-digit second
 * - SSS: 3-digit millisecond
 * 
 * @example
 * createCustomTimestamp('YYYY-MM-DD_HH-mm-ss') // Returns: '2024-01-15_14-30-25'
 * createCustomTimestamp('YYYYMMDD_HHmmss') // Returns: '20240115_143025'
 */
function createCustomTimestamp(format: string): string {
  const now = new Date();

  const replacements = {
    'YYYY': now.getFullYear().toString(),
    'MM': (now.getMonth() + 1).toString().padStart(2, '0'),
    'DD': now.getDate().toString().padStart(2, '0'),
    'HH': now.getHours().toString().padStart(2, '0'),
    'mm': now.getMinutes().toString().padStart(2, '0'),
    'ss': now.getSeconds().toString().padStart(2, '0'),
    'SSS': now.getMilliseconds().toString().padStart(3, '0')
  };

  let result = format;
  for (const [placeholder, value] of Object.entries(replacements)) {
    result = result.replace(new RegExp(placeholder, 'g'), value);
  }

  return result;
}

/**
 * Adds a custom timestamp to filename using a format string
 * @param file - The File object to rename
 * @param format - Custom timestamp format
 * @returns A new File object with the custom timestamped filename
 * 
 * @example
 * addCustomTimestampToFilename(file, 'YYYY-MM-DD_HH-mm-ss') 
 * // 'document.pdf' -> 'document_2024-01-15_14-30-25.pdf'
 */
export function addTimestampToFilename(file: File): File {
  if (!file || !file.name) {
    return file;
  }

  const timestamp = createCustomTimestamp('YYYY-MM-DD_HH-mm-ss');
  const originalName = file.name;

  // Find the last dot to separate name from extension
  const lastDotIndex = originalName.lastIndexOf('.');

  let newFilename: string;
  if (lastDotIndex === -1) {
    // No extension found
    newFilename = `${originalName}_${timestamp}`;
  } else {
    // Split filename and extension
    const nameWithoutExt = originalName.substring(0, lastDotIndex);
    const extension = originalName.substring(lastDotIndex);
    newFilename = `${nameWithoutExt}_${timestamp}${extension}`;
  }

  // Create a new File object with the same content but new name
  return new File([file], newFilename, {
    type: file.type,
    lastModified: file.lastModified
  });
}


/**
 * Extracts the file key for deletion from a URL
 * The file key consists of the last two path segments (customerRef/filename)
 * @param url - The full URL to extract the file key from
 * @returns The file key in format "customerRef/filename" or null if invalid
 * 
 * @example
 * extractFileKeyForDeletion('https://travel-stage.untrackedtrips.it/LorenzoVespri/mappa-croazia-codici-postali_2025-10-05_11-09-06.pdf')
 * // Returns: 'LorenzoVespri/mappa-croazia-codici-postali_2025-10-05_11-09-06.pdf'
 * 
 * extractFileKeyForDeletion('https://example.com/path/to/CustomerName/document.pdf')
 * // Returns: 'CustomerName/document.pdf'
 * 
 * extractFileKeyForDeletion('invalid-url')
 * // Returns: null
 */
export function extractFileKeyForDeletion(url: string): string {
  try {
    // Remove query parameters and hash fragments
    const cleanUrl = url.split('?')[0].split('#')[0];
    
    // Split by slash and remove empty parts
    const pathParts = cleanUrl.split('/').filter(part => part.length > 0);
    
    // We need at least 3 parts: protocol, domain, customerRef, filename
    // Or at least 2 parts if it's a relative path: customerRef, filename
    if (pathParts.length < 2) {
      throw new Error('URL does not contain enough path segments');
    }
    
    // Get the last two parts (customerRef and filename)
    const customerRef = pathParts[pathParts.length - 2];
    const filename = pathParts[pathParts.length - 1];
    
    // Validate that both parts exist and are not empty
    if (!customerRef || !filename) {
      throw new Error('URL does not contain enough path segments');
    }
    
    return `${customerRef}/${filename}`;
    
  } catch (error) {
    console.error('Error extracting file key from URL:', url, error);
    return '';
  }
}